import { Box, Card, CardContent, List, ListItem, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { NotSerializedBookingInputs } from "../../features/Booking-Form/typings/booking-inputs";
import { FormConfirmingSpinner } from "./partial/form-confirming-spinner.partial";
import { InfoRow } from "./vehicle-information-card-contents.component";
import { visitorConfirmCompanionFloorPlanVisible } from "./functions/team-visitor-invitation.functions";

/** Typical height of one visitor block in the confirm card (px). */
const VISITOR_CONFIRM_ROW_ESTIMATE_PX = 188;

/** How many visitor rows to show before scrolling (fractional row hints more content below). */
const VISITOR_CONFIRM_VISIBLE_ITEM_FRACTION = 1.8;

const visitorConfirmListMaxHeightPx =
  VISITOR_CONFIRM_ROW_ESTIMATE_PX * VISITOR_CONFIRM_VISIBLE_ITEM_FRACTION;

type P = {
  confirmingBooking: boolean;
  bookingInputs: NotSerializedBookingInputs;
};

type FloorsSlice = RootState["booking"]["floors"];

/**
 * Resolves the human-readable floor label from Redux floor list + current selection.
 */
export function resolveFloorDisplayNameForVisitorConfirm(
  floors: FloorsSlice | undefined,
  floorInventoryId: number | null | undefined
): string | undefined {
  const inventory = floors?.inventory ?? [];
  const idx = floors?.currentFloorIndex;
  if (idx !== undefined && inventory[idx]?.name) {
    return inventory[idx].name;
  }
  if (floorInventoryId != null) {
    return inventory.find(f => f.id === floorInventoryId)?.name;
  }
  return undefined;
}

export const FormVisitorConfirm: React.FC<P> = ({ confirmingBooking, bookingInputs }) => {
  const { t } = useTranslation();
  const registrations = bookingInputs.visitorRegistrations ?? [];
  const floors = useSelector((state: RootState) => state.booking.floors);

  const bookPlaceLocationDisplay = bookingInputs.locationName?.trim() ?? "";
  const bookPlaceFloorDisplay = useMemo(
    () =>
      resolveFloorDisplayNameForVisitorConfirm(floors, bookingInputs.floorInventoryId)?.trim() ??
      "",
    [floors, bookingInputs.floorInventoryId]
  );

  const companionFloorPlanVisible = useMemo(
    () => visitorConfirmCompanionFloorPlanVisible(bookingInputs),
    [bookingInputs]
  );

  const visitorListMaxHeight = companionFloorPlanVisible ? visitorConfirmListMaxHeightPx : "66vh";

  if (confirmingBooking) {
    return <FormConfirmingSpinner />;
  }

  if (registrations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("Not set")}
      </Typography>
    );
  }

  return (
    <Card
      data-testid="visitor-confirm-card"
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2,
        border: theme => `1px solid ${theme.palette.divider}`
      }}
    >
      <CardContent
        sx={{
          pb: 0,
          "&:last-child": { pb: 2 },
          display: "flex",
          flexDirection: "column",
          minHeight: 0
        }}
      >
        <Box
          sx={{
            maxHeight: visitorListMaxHeight,
            overflowY: "auto",
            pr: 0.5
          }}
        >
          <List sx={{ p: 0 }}>
            {registrations.map((visitor, index) => {
              const visitReasonTrimmed = String(visitor.visitReason ?? "").trim();
              const isBookPlace = visitor.bookPlace === true;

              return (
                <ListItem
                  key={visitor.id}
                  disableGutters
                  sx={theme => ({
                    alignItems: "flex-start",
                    display: "block",
                    py: 1,
                    px: 1.5,
                    my: 1,
                    borderRadius: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.default
                  })}
                >
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {t("_visitorBooking_visitorLabel", { n: index + 1 })}
                    </Typography>
                    <InfoRow label={t("Name")} value={`${visitor.firstName} ${visitor.surname}`} />
                    {isBookPlace ? (
                      <>
                        <InfoRow
                          label={t("Location")}
                          value={
                            bookPlaceLocationDisplay !== ""
                              ? bookPlaceLocationDisplay
                              : t("Not set")
                          }
                        />
                        <InfoRow
                          label={t("Floor")}
                          value={
                            bookPlaceFloorDisplay !== "" ? bookPlaceFloorDisplay : t("Not set")
                          }
                        />
                      </>
                    ) : (
                      <InfoRow
                        label={t("Location")}
                        value={visitor.visitOnlyLocationName?.trim() || t("Not set")}
                      />
                    )}
                    {visitor.company && <InfoRow label={t("Company")} value={visitor.company} />}
                    <InfoRow label={t("_visitorBooking_email")} value={visitor.email} />
                    <InfoRow label={t("_visitorBooking_contacts")} value={visitor.contacts} />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", md: "flex-start" },
                        gap: { xs: 0.5, md: 2 }
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                          flexShrink: 0,
                          whiteSpace: "nowrap"
                        }}
                      >
                        {t("_visitorBooking_visitReason")}
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        sx={{
                          width: { xs: "100%", md: "auto" },
                          flex: { md: 1 },
                          minWidth: 0,
                          textAlign: { xs: "left", md: "right" },
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere"
                        }}
                      >
                        {visitReasonTrimmed !== "" ? visitReasonTrimmed : t("Not set")}
                      </Typography>
                    </Box>
                  </Stack>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </CardContent>
    </Card>
  );
};
